import { AfterViewInit, Component, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ApiService } from '../services/api.service';
import * as XLSX from 'xlsx';
import { TestArea } from 'src/app/common/test-area';
import { ErrorHelper } from '../common/ErrorHelper';
import { NotifierService } from 'angular-notifier';
import { PdfexportType } from '../common/pdfexport-type';
import { User } from '../model/user';
import { AppComponent } from '../app.component';
import { log } from 'console';

export interface StaticImage {
  imageName: string;
  path: string;
  noHostUrl: string;
  url: string;
}

export interface StaticFilter {
  brand: string;
  carLine: string;
  majorMinorIndicator: string;
  bodyStyle: string;
}

@Component({
  selector: 'app-static',
  templateUrl: './test-static.component.html',
  styleUrls: ['./test-static.component.scss']
})
export class StaticComponent implements AfterViewInit {

  displayedColumns: string[] = ['imageName', 'path', 'url'];
  dataSource: MatTableDataSource<StaticImage>;
  staticForm: FormGroup;

  isSubmitted = false;
  isPDFLoading = false;
  isLoading = true;

  optionType = 'Colors';
  optionTypes: Array<string> = ['Colors', 'Trims', 'Features', 'CO2', 'Dimensions'];
  paramMap = new Map();
  explicit: boolean;
  showImages: boolean;

  co2Filters: StaticFilter[];
  colorFilters: StaticFilter[];
  trimFilters: StaticFilter[];
  featureFilters: StaticFilter[];
  dimensionFilters: StaticFilter[];

  dropDownList: StaticFilter[];
  brands: any[];
  carlines: any[];
  majorMinorIndicators: any[];
  bodyStyles: any[];

  pdfExportType: PdfexportType;
  pdfExportBrand: string;
  pdfExportModel: string;

  testArea = TestArea;
  selectedClass: string;
  selectedTestArea: TestArea;
  user = new User();

  @ViewChild(MatSort) sort: MatSort;

  constructor(private apiService: ApiService,
    private notifierService: NotifierService,
    private formBuilder: FormBuilder,
    private app: AppComponent) {

    this.dataSource = new MatTableDataSource();
    this.app.getUserData().then(user => {
      this.user = user;
      if (user.hasEmbargoPermissions()) {
        this.selectedClass = 'embargo';
        this.selectedTestArea = TestArea.EMBARGO;
      } else {
        this.selectedClass = 'preprod';
        this.selectedTestArea = TestArea.PREPROD;
      }
    });
    this.createAndLoadFormWithInitialValues();
  }

  createAndLoadFormWithInitialValues(): void {
    this.staticForm = this.formBuilder.group({
      optionType: 'Colors',
      searchMode: 'implicit',
      showImages: true,
      model: [''],
      bodyStyle: [''],
      brand: [''],
      majorMinor: ['']
    });
  }

  ngAfterViewInit(): void {
    this.isLoading = true;
    this.dataSource.sort = this.sort;
    this.staticForm.controls.brand.disable();
    this.staticForm.controls.majorMinor.disable();
    this.pdfExportType = PdfexportType.COLORS;
    this.pdfExportBrand = '';
    this.pdfExportModel = '';
    this.loadVersionsCriteriaAsync();
  }

  selectionChanged(item): void {
    this.optionType = item.value;

    this.staticForm.controls.brand.setValue('');
    this.staticForm.controls.model.setValue('');
    this.staticForm.controls.majorMinor.setValue('');
    this.staticForm.controls.bodyStyle.setValue('');

    if (this.optionType === 'Features') {
      this.staticForm.controls.model.enable();
      this.staticForm.controls.bodyStyle.enable();
      this.staticForm.controls.majorMinor.enable();
      this.staticForm.controls.brand.enable();
      this.pdfExportType = PdfexportType.FEATURES;
      this.dropDownList = this.featureFilters;
    } else if (this.optionType === 'Colors') {
      this.staticForm.controls.brand.disable();
      this.staticForm.controls.majorMinor.disable();
      this.pdfExportType = PdfexportType.COLORS;
      this.pdfExportBrand = '';
      this.pdfExportModel = '';
      this.dropDownList = this.colorFilters;

    } else if (this.optionType === 'CO2') {
      this.staticForm.controls.model.disable();
      this.staticForm.controls.bodyStyle.disable();
      this.staticForm.controls.majorMinor.disable();
      this.staticForm.controls.brand.disable();
      this.pdfExportType = PdfexportType.CO2;
      this.pdfExportBrand = '';
      this.pdfExportModel = '';
      this.dropDownList = this.co2Filters;

    } else if (this.optionType === 'Dimensions') {
      this.staticForm.controls.model.disable();
      this.staticForm.controls.bodyStyle.disable();
      this.staticForm.controls.majorMinor.disable();
      this.staticForm.controls.brand.enable();
      // this.pdfExportType = PdfexportType.DIMENSIONS;
      // this.pdfExportBrand = '';
      // this.pdfExportModel = '';
      this.dropDownList = this.dimensionFilters;


    } else {
      this.staticForm.controls.brand.disable();
      this.staticForm.controls.majorMinor.disable();
      this.pdfExportType = PdfexportType.TRIMS;
      this.pdfExportBrand = '';
      this.pdfExportModel = '';
      this.dropDownList = this.trimFilters;
    }
    this.buildListsForDropdown();
  }

  buildListsForDropdown(): void {
    // filter out duplicates and null/empty values
    this.brands = Array.from(new Set(this.dropDownList.map(e => e.brand).filter(e => e)));
    this.carlines = Array.from(new Set(this.dropDownList.map(e => e.carLine).filter(e => e)));
    this.majorMinorIndicators = Array.from(new Set(this.dropDownList.map(e => e.majorMinorIndicator).filter(e => e)));
    this.bodyStyles = Array.from(new Set(this.dropDownList.map(e => e.bodyStyle).filter(e => e)));
  }

  onBrandSelected(brand): void {
    this.staticForm.controls.model.setValue('');
    this.staticForm.controls.majorMinor.setValue('');
    this.staticForm.controls.bodyStyle.setValue('');
    this.pdfExportBrand = brand;

    this.carlines = Array.from(new Set(this.dropDownList
      .filter(e => e.brand === brand)
      .map(e => e.carLine)
      .filter(e => e)));
    this.majorMinorIndicators = Array.from(new Set(this.dropDownList
      .filter(e => e.brand === brand && this.carlines.includes(e.carLine))
      .map(e => e.majorMinorIndicator)
      .filter(e => e)));
    this.bodyStyles = Array.from(new Set(this.dropDownList
      .filter(e => e.brand === brand && this.carlines.includes(e.carLine) && this.majorMinorIndicators.includes(e.majorMinorIndicator))
      .map(e => e.bodyStyle)
      .filter(e => e)));
  }

  onCarlineSelected(carline): void {
    this.staticForm.controls.majorMinor.setValue('');
    this.staticForm.controls.bodyStyle.setValue('');
    this.pdfExportModel = carline;

    this.majorMinorIndicators = Array.from(new Set(this.dropDownList
      .filter(e => e.carLine === carline)
      .map(e => e.majorMinorIndicator)
      .filter(e => e)));
    this.bodyStyles = Array.from(new Set(this.dropDownList
      .filter(e => e.carLine === carline && this.majorMinorIndicators.includes(e.majorMinorIndicator))
      .map(e => e.bodyStyle)
      .filter(e => e)));
  }

  onMajorMinorSelected(majorMinor): void {
    this.staticForm.controls.bodyStyle.setValue('');

    this.bodyStyles = Array.from(new Set(this.dropDownList
      .filter(e => e.majorMinorIndicator === majorMinor)
      .map(e => e.bodyStyle)
      .filter(e => e)));
  }

  applyFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  onSubmit(buttonType): void {
    if (buttonType === 'ui') {
      this.submitUI();
    }
    if (buttonType === 'pdf') {
      this.submitPDF();
    }
  }

  async submitPDF(): Promise<void> {
    console.log('submitPDF');
    switch (this.pdfExportType) {
      case 'Colors':
      case 'Trims':
      case 'Dimensions':
      case 'CO2':
        try {
          this.isPDFLoading = true;
          await this.createPDF();
        } catch (error) {
          console.log(error);
        } finally {
          this.isPDFLoading = false;
        }
        break;
      case 'Features':
        if (this.pdfExportBrand !== '' && this.pdfExportModel !== '') {
          try {
            this.isPDFLoading = true;
            await this.createFeaturesPDF();
          } catch (error) {
            console.log(error);
          } finally {
            this.isPDFLoading = false;
          }
        } else {
          const errorMessage = ErrorHelper.disectError('PDF Download', 'Please select brand and model');
          this.notifierService.notify('error', errorMessage);
        }
        break;
    }
  }

  preparePDF(response): void {
    const contentDisposition = response.headers.get('content-disposition');
    const pdfName = contentDisposition.split(';')[1].split('filename')[1].split('=')[1].trim();
    const blob = new Blob([response.body], { type: 'application/pdf' });
    const link = document.createElement('a');

    link.href = window.URL.createObjectURL(blob);
    link.download = pdfName;
    link.click();
  }

  async createPDF(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      return this.apiService.getPDF(this.pdfExportType, this.selectedTestArea)
        .subscribe(
          (response) => {
            this.preparePDF(response);
            resolve(true);
          },
          (error) => {
            const errorMessage = ErrorHelper.disectError('getPDF ', error);
            this.notifierService.notify('error', errorMessage);
            resolve(false);
          });
    });
  }

  async createFeaturesPDF(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      return this.apiService.getPDFFeatures(this.pdfExportBrand, this.pdfExportModel, this.selectedTestArea)
        .subscribe((response) => {
          this.preparePDF(response);
          resolve(true);
        },
          (error) => {
            const errorMessage = ErrorHelper.disectError('getPDFFeatures', error);
            this.notifierService.notify('error', errorMessage);
            resolve(false);
          });
    });
  }

  submitUI(): void {


    if (this.optionType === 'Dimensions' && this.staticForm.get('brand').value.trim() === '') {
      this.notifierService.notify('error', 'Please select a brand for Dimensions');
      return;
    }


    this.paramMap.clear();


    if (this.staticForm.controls.model.enabled && this.staticForm.controls.model.value) {
      this.paramMap.set('familyCode', this.staticForm.controls.model.value);
    }
    if (this.staticForm.controls.bodyStyle.enabled && this.staticForm.controls.bodyStyle.value) {
      this.paramMap.set('bodyStyle', this.staticForm.controls.bodyStyle.value);
    }
    if (this.staticForm.controls.brand.enabled && this.staticForm.controls.brand.value) {
      this.paramMap.set('brand', this.staticForm.controls.brand.value);
    }
    if (this.staticForm.controls.majorMinor.enabled && this.staticForm.controls.majorMinor.value) {
      this.paramMap.set('majorIndicator', this.staticForm.controls.majorMinor.value.substring(0, 1));
      this.paramMap.set('minorIndicator', this.staticForm.controls.majorMinor.value.substring(1, 2));
    }
    this.explicit = this.staticForm.controls.searchMode.value === 'explicit';
    this.showImages = this.staticForm.controls.showImages.value;

    if (this.optionType === 'CO2') {
      this.loadCO2ImagesAsync();
    } else {
      this.loadDataAsync();
    }
    this.isSubmitted = true;
  }

  async loadVersionsCriteriaAsync(): Promise<any> {
    try {
      await this.loadVersionsCriteria();
    } catch (error) {
      console.log(error);
    } finally {
      this.dropDownList = this.featureFilters;
      this.buildListsForDropdown();
      this.isLoading = false; // Hide loader after data is loaded
    }
  }

  loadVersionsCriteria(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      return this.apiService.getVersionsStaticCriteria(this.selectedTestArea).toPromise().then(
        (data) => {
          this.colorFilters = data.colorFilters;
          this.trimFilters = data.trimFilters;
          this.featureFilters = data.featureFilters;
          this.dimensionFilters = data.dimensionFilters;
          console.log('Fetched the versions criteria');
          resolve(true);
        },
        (error) => {
          const errorMessage = ErrorHelper.disectError('get version criteria', error);
          this.notifierService.notify('error', errorMessage);
          resolve(false);
        });
    });
  }

  async loadCO2ImagesAsync(): Promise<any> {
    try {
      await this.loadCO2Images();
    } catch (error) {
      console.log(error);
    }
  }

  loadCO2Images(): Promise<boolean> {
    console.log(this.selectedTestArea);
    return new Promise<boolean>((resolve, reject) => {
      return this.apiService.getStaticCO2Images(this.explicit, this.selectedTestArea)
        .toPromise().then(
          (data) => {


            const images = data;
            images.forEach(element => {
              if (!element.url.startsWith('https')) {
                element.url = element.url.replace('http', 'https');
              }
            });
            console.log(images);
            this.dataSource = new MatTableDataSource(images);
            this.dataSource.sort = this.sort;
            console.log('Fetched the static CO2Class images');
            resolve(true);
          },
          (error) => {
            const errorMessage = ErrorHelper.disectError('load static CO2Class image info', error);
            this.notifierService.notify('error', errorMessage);
            resolve(false);
          });
    });
  }

  async loadDataAsync(): Promise<any> {
    try {
      await this.loadData();
    } catch (error) {
      console.log(error);
    }
  }

  loadData(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      return this.apiService.getStaticOptionImageInfoBasedOnOptionType(this.optionType, this.paramMap, this.explicit, this.selectedTestArea)
        .toPromise().then(
          (data) => {
            const images = data;
            images.forEach(element => {
              if (!element.url.startsWith('https')) {
                element.url = element.url.replace('http', 'https');
              }
            });
            console.log(images);
            this.dataSource = new MatTableDataSource(images);
            this.dataSource.sort = this.sort;
            console.log('Fetched the static images');
            resolve(true);
          },
          (error) => {
            const errorMessage = ErrorHelper.disectError('load static image info', error);
            this.notifierService.notify('error', errorMessage);
            resolve(false);
          });
    });
  }

  exportTable(): void {
    const timeSpan = new Date().toLocaleString();
    const sheetName = 'TestStaticImagesV2';
    const fileName = `${sheetName}-${timeSpan}`;
    const targetTable = document.getElementById('staticImagesTableV2');

    const ws: XLSX.WorkSheet = XLSX.utils.table_to_sheet(targetTable);
    ws['!cols'][2] = { hidden: true };
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    XLSX.writeFile(wb, `${fileName}.xlsx`);
  }

  selectTestArea(test: TestArea): void {
    this.selectedTestArea = test;
    this.selectedClass = this.selectedTestArea.toLowerCase().split(' ').find(e => true);
    this.createAndLoadFormWithInitialValues();
    this.staticForm.controls.brand.disable();
    this.staticForm.controls.majorMinor.disable();
    this.loadVersionsCriteriaAsync();
    this.dataSource = new MatTableDataSource();
  }
}

